/* global chrome */
import React, { useState } from 'react';
import { Download, Loader2, CheckCircle2, AlertCircle, Linkedin } from 'lucide-react';
import { toast } from 'react-toastify';

const LinkedInSettings = () => {
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [lastImport, setLastImport] = useState(null);

  const handleImportConnections = async () => {
    // Vérifier si l'extension Chrome est disponible
    if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
      toast.error('Extension Chrome requise. Assurez-vous que l\'extension Jobrise est installée et activée.');
      return;
    }

    setImporting(true);
    setProgress({ current: 0, status: 'starting' });

    const EXTENSION_ID = 'ahggehojcdmojmdoacambcmchehpiioo'; // ID de l'extension Jobrise

    try {
      // Envoyer le message à l'extension pour importer les connexions
      chrome.runtime.sendMessage(
        EXTENSION_ID,
        {
          type: 'import-linkedin-connections',
          data: {},
        },
        async (response) => {
          if (chrome.runtime.lastError) {
            console.error('Extension error:', chrome.runtime.lastError);
            toast.error('Extension non détectée. Assurez-vous que l\'extension Jobrise est installée.');
            setImporting(false);
            setProgress(null);
            return;
          }

          if (response && response.success) {
            console.log(`${response.total} connexions récupérées:`, response.connections);

            setProgress({
              current: response.total,
              status: 'fetched',
              total: response.total
            });

            // Envoyer les connexions au backend Jobrise pour les sauvegarder
            try {
              setProgress({
                current: response.total,
                status: 'saving',
                total: response.total
              });

              const result = await window.API.post('/api/individuals/bulk-import', {
                connections: response.connections,
              });

              if (result.data.success) {
                toast.success(`${result.data.imported} connexions importées avec succès !`);
                if (result.data.skipped > 0) {
                  toast.info(`${result.data.skipped} profils déjà existants ignorés`);
                }

                setLastImport({
                  date: new Date(),
                  imported: result.data.imported,
                  skipped: result.data.skipped,
                  total: response.total
                });

                setProgress({
                  current: response.total,
                  status: 'completed',
                  imported: result.data.imported,
                  skipped: result.data.skipped
                });
              }
            } catch (error) {
              console.error('Error saving connections:', error);
              toast.error('Erreur lors de la sauvegarde des connexions dans la CVthèque');
              setProgress(null);
            }
          } else {
            toast.error(response?.error || 'Erreur lors de l\'import des connexions');
            setProgress(null);
          }

          setImporting(false);
        }
      );
    } catch (error) {
      console.error('Error importing connections:', error);
      toast.error('Une erreur est survenue lors de l\'import');
      setImporting(false);
      setProgress(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <Linkedin className="text-blue-600" size={24} />
          </div>
          <h2 className="text-xl font-semibold text-slate-800">LinkedIn</h2>
        </div>
        <p className="text-sm text-slate-600">
          Gérez vos intégrations et imports LinkedIn
        </p>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Import des connexions */}
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-medium text-slate-800 mb-2">
              Importer mes connexions LinkedIn
            </h3>
            <p className="text-sm text-slate-600">
              Importez toutes vos connexions LinkedIn directement dans votre CVthèque Jobrise.
              Vos connexions seront automatiquement ajoutées à vos contacts.
            </p>
          </div>

          {/* Bouton d'import */}
          <button
            onClick={handleImportConnections}
            disabled={importing}
            className={`
              flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-all
              ${importing
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg'
              }
            `}
          >
            {importing ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                <span>Import en cours...</span>
              </>
            ) : (
              <>
                <Download size={20} />
                <span>Importer mes connexions</span>
              </>
            )}
          </button>

          {/* Progress indicator */}
          {progress && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                {progress.status === 'completed' ? (
                  <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                ) : (
                  <Loader2 className="text-blue-600 animate-spin flex-shrink-0 mt-0.5" size={20} />
                )}
                <div className="flex-1">
                  {progress.status === 'starting' && (
                    <p className="text-sm text-slate-700">Démarrage de l'import...</p>
                  )}
                  {progress.status === 'fetched' && (
                    <p className="text-sm text-slate-700">
                      {progress.total} connexions récupérées depuis LinkedIn
                    </p>
                  )}
                  {progress.status === 'saving' && (
                    <p className="text-sm text-slate-700">
                      Sauvegarde de {progress.total} connexions dans votre CVthèque...
                    </p>
                  )}
                  {progress.status === 'completed' && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-green-700">
                        Import terminé avec succès !
                      </p>
                      <p className="text-xs text-slate-600">
                        {progress.imported} nouvelles connexions importées
                        {progress.skipped > 0 && ` • ${progress.skipped} déjà existantes`}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Last import info */}
          {lastImport && !importing && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-slate-600">
                <span className="font-medium">Dernier import :</span>{' '}
                {new Date(lastImport.date).toLocaleString('fr-FR')}
                {' • '}
                {lastImport.imported} profils importés
              </p>
            </div>
          )}
        </div>

        {/* Info box */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm text-slate-700 space-y-2">
              <p className="font-medium">Points importants :</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>L'extension Chrome Jobrise doit être installée et activée</li>
                <li>Vous devez être connecté à LinkedIn dans votre navigateur</li>
                <li>L'import peut prendre plusieurs minutes selon le nombre de connexions</li>
                <li>Les profils déjà existants dans votre CVthèque ne seront pas dupliqués</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkedInSettings;
